import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import HubOperatorLayout from "@/components/HubOperatorLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  CheckCircle, Loader2, RefreshCw, MapPin, Calendar, AlertTriangle
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const HubOperatorCompleted = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const res = await axios.get(`${API}/inward/operator/sessions`, {
        params: { status: "COMPLETED,FORCE_COMPLETED" }
      });
      setSessions(res.data.sessions || []);
    } catch (e) {
      console.error("Failed to fetch sessions:", e);
      toast.error("Failed to load sessions");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString("en-IN", { 
        day: "numeric", 
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return iso;
    }
  };

  if (loading) {
    return (
      <HubOperatorLayout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </HubOperatorLayout>
    );
  }

  return (
    <HubOperatorLayout user={user} onLogout={onLogout}>
      <div className="space-y-4" data-testid="hub-operator-completed">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-800">Completed</h1>
            <p className="text-sm text-slate-500">{sessions.length} completed session(s)</p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => fetchSessions(true)}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Sessions List */}
        {sessions.length === 0 ? (
          <Card className="border-0 shadow-md">
            <CardContent className="py-12 text-center">
              <CheckCircle className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No completed sessions yet</p>
              <p className="text-sm text-slate-400 mt-1">Completed sessions will appear here</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {sessions.map((session) => {
              const inwarded = session.inwarded_shipments?.length || session.inwarded_count || 0;
              const total = session.total_shipments || 0;
              const isForceCompleted = session.status === "FORCE_COMPLETED";
              
              return (
                <Card key={session._id} className={`border-0 shadow-md overflow-hidden border-l-4 ${
                  isForceCompleted ? "border-l-amber-500" : "border-l-emerald-500"
                }`}>
                  <CardContent className="p-4">
                    {/* Session Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-slate-800">{session.session_code}</h3>
                          {isForceCompleted ? (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">
                              <AlertTriangle className="w-3 h-3 mr-1" /> Force Completed
                            </Badge>
                          ) : (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                              <CheckCircle className="w-3 h-3 mr-1" /> Completed
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          Completed {formatDate(session.completed_at || session.force_completed_at)}
                        </p>
                      </div>
                    </div>

                    {/* Session Stats */}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div className="bg-slate-50 rounded-lg p-2 text-center">
                        <p className="text-lg font-bold text-blue-600">{session.total_trips || 0}</p>
                        <p className="text-[10px] text-slate-500 uppercase">Trips</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-2 text-center">
                        <p className="text-lg font-bold text-slate-700">{total}</p>
                        <p className="text-[10px] text-slate-500 uppercase">Total</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-2 text-center">
                        <p className={`text-lg font-bold ${isForceCompleted && inwarded < total ? "text-amber-600" : "text-emerald-600"}`}>
                          {inwarded}
                        </p>
                        <p className="text-[10px] text-slate-500 uppercase">Inwarded</p>
                      </div>
                    </div>

                    {/* Force Completed Warning */}
                    {isForceCompleted && inwarded < total && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3">
                        <p className="text-xs text-amber-700">
                          <AlertTriangle className="w-3 h-3 inline mr-1" />
                          {total - inwarded} parcel(s) were not scanned when this session was force completed.
                        </p>
                        {session.force_completed_by && (
                          <p className="text-xs text-amber-600 mt-1">
                            By: {session.force_completed_by}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Hub Info */}
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <MapPin className="w-3.5 h-3.5" />
                      <span>{session.hub_name || "Unknown Hub"}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </HubOperatorLayout>
  );
};

export default HubOperatorCompleted;
